'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { StoreLink } from '@/components/store';
import { ContactDetails } from './contact-details';
import { CUSTOMER_SERVICE_LINKS } from '@/lib/customer-service';
import { cn } from '@cobrastyle/ui';


/**
 * The utility bar's "Kundtjänst" dropdown: a link list beside a contact card.
 *
 * Opens on hover for pointers and on click for keyboard and touch, where there
 * is no hover to open it with. `focus-within` is not enough on its own — the
 * panel is only in the DOM while open, so a keyboard user could never reach it
 * to focus anything inside; the click toggle is what puts it there.
 *
 * The close-on-blur check is deferred a tick because focus moves out of the
 * trigger *before* it lands on the panel link, so reading `activeElement`
 * synchronously would always see `body` and close the panel the moment a
 * keyboard user tabbed into it.
 */
export function CustomerServiceMenu() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const closeIfFocusLeft = () => {
    setTimeout(() => {
      const root = containerRef.current;
      if (root && !root.contains(document.activeElement)) setOpen(false);
    }, 0);
  };

  const t = useTranslations('nav');
  const tFooter = useTranslations('footer');

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onBlur={closeIfFocusLeft}
    >
      {/* A link, not a disclosure button. The section has no landing page, so
          this goes to the first page in it — which is also where the panel's
          own first link goes.

          That trades away the click-to-open the button gave keyboard and touch
          users, so the panel opens on FOCUS instead: tabbing to this link
          reveals it, and tabbing on walks straight into its links. Enter
          navigates, which is what a link should do. `aria-expanded` is gone
          with the button — announcing a control as expandable while Enter
          navigates away would be a worse lie than saying nothing.

          Touch has no focus and no hover, so a tap just navigates. That is the
          acceptable end of this trade: the page it lands on carries the whole
          section in its sidebar, so nothing is unreachable — see
          cms/customer-service-layout.tsx. The bar is `lg:block` only, so this
          is tablets rather than phones. */}
      <StoreLink
        href={CUSTOMER_SERVICE_LINKS[0].href}
        onFocus={() => setOpen(true)}
        className={cn(
          'flex items-center gap-1.5 py-1.5 transition-colors hover:text-foreground',
          open && 'text-foreground'
        )}
      >
        {t('customerService')}
        <span
          aria-hidden="true"
          className={cn('text-[9px] transition-transform', open && 'rotate-180')}
        >
          ▾
        </span>
      </StoreLink>

      {open && (
        <div className="absolute right-0 top-full z-[70] grid w-[560px] grid-cols-[1fr_220px] overflow-hidden rounded-md border border-border bg-card text-left shadow-xl">
          <div className="flex flex-col p-5">
            <Eyebrow>{t('customerService')}</Eyebrow>
            {CUSTOMER_SERVICE_LINKS.map(({ href, key }) => (
              <StoreLink
                key={href}
                href={href}
                // Rows are separated by a hairline rather than spacing so the
                // list reads as a menu; the last one drops its rule.
                className="flex items-center justify-between border-b border-border py-2 text-[13.5px] text-muted transition-colors last:border-b-0 hover:text-accent-foreground"
              >
                {tFooter(key)}
                <span aria-hidden="true">→</span>
              </StoreLink>
            ))}
          </div>

          <div className="flex flex-col gap-3.5 border-l border-border bg-surface-subtle p-5">
            <Eyebrow>{tFooter('contactHeading')}</Eyebrow>
            <ContactDetails className="flex-1" />
          </div>
        </div>
      )}
    </div>
  );
}

/** The redesign's section kicker: small mono caps in the accent colour. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="pb-2.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-accent-foreground">
      {children}
    </span>
  );
}
